import { LayoutGrid } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';

import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import type { VideoProps } from '@/app/components/Video';
import Video from '@/app/components/Video';

interface Photo extends ImageProps {
  caption: ReactNode;
  video?: Partial<VideoProps>;
}

interface PhotoWallProps {
  readonly children: ReactNode;
  readonly photos: Photo[];
}

const PhotoWall = ({ children, photos }: PhotoWallProps): JSX.Element => {
  const prefersReducedMotion = useReducedMotion();
  const [playVideo, setPlayVideo] = useState<boolean>(false);

  return (
    <LayoutGrid rowGap={64} data-cy="photo-wall-grid">
      <div
        data-cy="photo-wall-intro"
        className="col-main lg:col-start-15 lg:col-end-main lg:row-start-1 lg:row-end-3"
      >
        {children}
      </div>

      {photos.map(({ video, caption, ...imageProps }, i) => (
        <Fragment key={imageProps.src}>
          <div
            className={cx('relative col-full lg:col-main', {
              'min-h-[36rem] lg:col-end-13 lg:row-start-1 lg:row-end-4':
                i === 0,
              'col-start-4 min-h-[30rem] lg:col-start-15 lg:col-end-main lg:row-start-3 lg:row-end-5':
                i === 1,
              'col-end-8 min-h-[30rem] md:col-start-full md:col-end-13 lg:col-start-5 lg:col-end-13 lg:row-start-5 lg:row-end-7':
                i === 2,
            })}
          >
            {video && !prefersReducedMotion ? (
              <div data-cy="photo-wall-video">
                <Motion
                  onViewportEnter={() => setPlayVideo(true)}
                  onViewportLeave={() => setPlayVideo(false)}
                >
                  <Video
                    type="video/mp4"
                    contentTitle="pdp_video"
                    controls={false}
                    loop={false}
                    location="careers-photowall"
                    playsInline={true}
                    muted={true}
                    autoPlay={true}
                    {...video}
                    playVideo={playVideo}
                    altImage={{
                      objectFit: 'cover',
                      layout: 'fill',
                      ...imageProps,
                    }}
                  />
                </Motion>
              </div>
            ) : (
              <Image
                data-cy="photo-wall-image"
                fill
                className="object-cover"
                {...imageProps}
              />
            )}
          </div>

          <div
            className={cx('col-main', {
              'pb-12 lg:col-start-main lg:col-end-11 lg:row-start-4 lg:pb-8 xl:col-end-8':
                i === 0,
              'col-start-4 pb-12 lg:col-start-15 lg:col-end-main lg:row-start-5 lg:pb-8':
                i === 1,
              'col-end-8 md:col-end-13 lg:col-start-5 lg:row-start-7': i === 2,
            })}
          >
            {caption}
          </div>
        </Fragment>
      ))}
    </LayoutGrid>
  );
};

export default PhotoWall;
